import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Alert,
  Typography,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Stack,
  SelectChangeEvent
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import { apiService, Bundle } from '../services/api';

interface CreateClientModalProps {
  open: boolean;
  onClose: () => void;
  adminSecret: string;
  onClientCreated: () => void;
}

const CreateClientModal: React.FC<CreateClientModalProps> = ({
  open,
  onClose,
  adminSecret,
  onClientCreated
}) => {
  const [clientName, setClientName] = useState('');
  const [operatorSpaceLink, setOperatorSpaceLink] = useState('');
  const [selectedBundles, setSelectedBundles] = useState<Bundle[]>([]);
  const [currentBundleId, setCurrentBundleId] = useState<number | ''>('');
  const [availableBundles, setAvailableBundles] = useState<Bundle[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [createdClient, setCreatedClient] = useState<{ id: number; tx_name: string; tx_client_secret: string } | null>(null);

  // Fetch available bundles when modal opens
  useEffect(() => {
    if (open && adminSecret) {
      fetchBundles();
    }
  }, [open, adminSecret]);

  const fetchBundles = async () => {
    try {
      const bundles = await apiService.getAllBundles(adminSecret);
      setAvailableBundles(bundles);
    } catch (error) {
      console.error('Failed to fetch bundles:', error);
    }
  };

  const handleBundleSelect = (event: SelectChangeEvent<number | ''>) => {
    const bundleId = event.target.value;
    if (bundleId !== '') {
      const bundleToAdd = availableBundles.find(b => b.id === bundleId);
      if (bundleToAdd && !selectedBundles.find(b => b.id === bundleToAdd.id)) {
        setSelectedBundles([...selectedBundles, bundleToAdd]);
        setCurrentBundleId('');
      }
    }
  };

  const handleRemoveBundle = (bundleId: number) => {
    setSelectedBundles(selectedBundles.filter(b => b.id !== bundleId));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const bundleIds = selectedBundles.map(b => b.id);
      const newClient = await apiService.createClient(
        adminSecret, 
        clientName,
        operatorSpaceLink || undefined,
        bundleIds
      );
      setCreatedClient(newClient);
      onClientCreated();
    } catch (error: any) {
      setError(error?.response?.data?.detail || 'Failed to create client');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setClientName('');
    setOperatorSpaceLink('');
    setSelectedBundles([]);
    setCurrentBundleId('');
    setError('');
    setCreatedClient(null);
    onClose();
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Create New Client
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {createdClient ? (
          <Box>
            <Alert severity="success" sx={{ mb: 2 }}>
              Client created successfully!
            </Alert>
            
            <Typography variant="h6" gutterBottom>
              Client Details:
            </Typography>
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Client ID: {createdClient.id}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Client Name: {createdClient.tx_name}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <TextField
                fullWidth
                label="Client Secret"
                value={createdClient.tx_client_secret}
                InputProps={{ readOnly: true }}
                variant="outlined"
                size="small"
              />
              <IconButton onClick={() => copyToClipboard(createdClient.tx_client_secret)}>
                <ContentCopyIcon />
              </IconButton>
            </Box>

          </Box>
        ) : (
          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Client Name"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              margin="normal"
              required
              disabled={isLoading}
            />
            
            <TextField
              fullWidth
              label="Operator Space Link (Optional)"
              value={operatorSpaceLink}
              onChange={(e) => setOperatorSpaceLink(e.target.value)}
              margin="normal"
              disabled={isLoading}
              placeholder="https://example.com/space"
              helperText="Optional link to the operator's space"
            />

            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
              Client Bundles (Optional)
            </Typography>

            {selectedBundles.length > 0 && (
              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
                {selectedBundles.map((bundle) => (
                  <Chip
                    key={bundle.id}
                    label={`${bundle.tx_name} (v${bundle.tx_version})`}
                    onDelete={() => handleRemoveBundle(bundle.id)}
                    deleteIcon={<DeleteIcon />}
                    variant="outlined"
                    sx={{
                      mb: 1,
                      borderColor: '#9ACD32',
                      color: '#9ACD32',
                      '& .MuiChip-deleteIcon': {
                        color: '#9ACD32',
                        '&:hover': {
                          color: '#7BA428'
                        }
                      }
                    }}
                  />
                ))}
              </Stack>
            )}

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="bundle-select-label">Select Bundle to Add</InputLabel>
              <Select
                labelId="bundle-select-label"
                value={currentBundleId}
                onChange={handleBundleSelect}
                label="Select Bundle to Add"
                disabled={isLoading}
              >
                <MenuItem value="">
                  <em>Choose a bundle...</em>
                </MenuItem>
                {availableBundles
                  .filter(bundle => !selectedBundles.find(sb => sb.id === bundle.id))
                  .map((bundle) => (
                    <MenuItem key={bundle.id} value={bundle.id}>
                      {bundle.tx_name} (v{bundle.tx_version})
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            {selectedBundles.length > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {selectedBundles.length} bundle(s) selected
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {createdClient ? (
          <Button onClick={handleClose} variant="contained">
            Close
          </Button>
        ) : (
          <>
            <Button onClick={handleClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={isLoading || !clientName}
            >
              {isLoading ? 'Creating...' : 'Create Client'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CreateClientModal;